/* ── IFC-markering: tolka id, normalisera egenskaper, trädnoder ── */

import { plainValue } from './ifcProperties';

/**
 * Tolkar ett markerings-id på formen "ifc:<modelID>:<expressID>".
 * @param {*} id
 * @returns {{modelID: number, expressID: number} | null}
 */
export function parseIfcSelectionId(id) {
  const s = String(id || '');
  if (!s.startsWith('ifc:')) return null;
  const parts = s.split(':');
  if (parts.length !== 3) return null;
  const modelID = Number(parts[1]);
  const expressID = Number(parts[2]);
  if (!Number.isFinite(modelID) || !Number.isFinite(expressID)) return null;
  return { modelID, expressID };
}

/**
 * Gör om motorns egenskaper (engine.getProperties) till det markeringsobjekt som panelen visar.
 * @returns {{id: string, name: string, ifcType: string, globalId: string, groups: Array}}
 */
export function normalizeIfcSelection(data, modelID, expressID) {
  return {
    id: `ifc:${modelID}:${expressID}`,
    name: data?.name || `ExpressID ${expressID}`,
    ifcType: data?.type || 'IFC-objekt',
    globalId: data?.globalId || '',
    groups: Array.isArray(data?.groups) ? data.groups : [],
  };
}

/**
 * Hämtar egenskaper för ett ExpressID och returnerar ett markeringsobjekt.
 * Vid fel returneras ett minimalt objekt med bara id:n.
 * @param {object} engine motorn (engine/createEngine)
 */
export async function fetchIfcSelection(engine, modelID, id) {
  try {
    const data = await engine.getProperties(id);
    return normalizeIfcSelection(data, modelID, id);
  } catch {
    return normalizeIfcSelection(null, modelID, id);
  }
}

export function getExpressId(node) {
  const v = node?.expressID ?? node?.expressId ?? node?.id;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Nodens namn: Name, annars LongName, annars typen. */
export function nodeLabel(node) {
  if (!node || typeof node !== 'object') return 'Objekt';
  const name = plainValue(node.Name) || plainValue(node.LongName) || (typeof node.name === 'string' ? node.name : '');
  if (name && name.trim()) return name.trim();
  if (typeof node.type === 'string' && node.type.trim()) return node.type;
  return 'Objekt';
}

/** Nodens IFC-typ som text (t.ex. "IFCWALL"), eller ''. */
export function nodeType(node) {
  return typeof node?.type === 'string' ? node.type : '';
}

/** Kontrollerar om filen ser ut som en IFC-fil (på filändelsen). Visaren klarar inte .ifczip. */
export function isIfcFile(file) {
  if (!file?.name) return true; // inget namn — anta att anroparen vet vad den gör
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return ext === 'ifc';
}
